import { computed, inject, Injectable, isDevMode, Signal } from '@angular/core';
import { Observable } from 'rxjs';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import {
	AanwezigheidUitsplitsing,
	AfwijkingPercentielWeergave,
	CijferColumn,
	CijferkolomKeuze,
	CijfersSeCeWeergave,
	CohortrendementType,
	CohortrendementWeergave,
	DashboardAspect,
	DashboardVariant,
	DoorstroomWeergave,
	Dossier,
	Eenheid,
	Interval,
	Tijdseenheid,
	VerschilBeoogd,
} from './weergave-opties';
import { DRIE_SCHOOLJAREN_EXCL_2020 } from '@cumlaude/shared-utils';
import { Sort } from '../shared/components/table/table/table.component';
import { isEqual, isUndefined } from 'lodash-es';
import { toSignal } from '@angular/core/rxjs-interop';
import { CurrentUrlService } from '@cumlaude/shared-services';
import { LeerlingSelectieId } from './leerling-selectie.service';
import { CumLaudeRoute } from '@cumlaude/shared-components-menu';
import { DatasnapResultKey, encodeKey } from './datasnap-url.service';
import { AttrPath, CategorieUitstroomniveauVSO, TussentijdseUitstroomVSO, VergelijkingUitstroomniveauVSO } from '@cumlaude/metadata';
import { UserRouteData } from './user-routes.service';
import { controleerQueryParamsUitRegistry } from './query-param-checks';
import { UserService } from './user.service';
import { findDashboardOption, optionDefaultForRol, optionValuesForRol } from '@cumlaude/shared-dashboard-registry';

type Codec<T> = {
	def: T;
	encode: (val: T) => string | string[];
	decode: (str: string[]) => T;
};

function singleCodec<T>(def: T): Codec<T> {
	return { def, encode: (x) => `${x}`, decode: (x) => <T>(<unknown>x[0]) };
}

function multiCodec<T>(def: T[]): Codec<T[]> {
	return { def, encode: (xs) => xs.map((x) => <string>(<unknown>x)), decode: (xs) => xs.map((x) => <T>(<unknown>x)) };
}

function numberCodec(def: number): Codec<number> {
	return { def, encode: (x) => `${x}`, decode: (x) => Number(x[0]) };
}

function numberOrUndefinedCodec(def: number | undefined): Codec<number | undefined> {
	return { def, encode: (x) => (x === undefined ? '' : `${x}`), decode: (x) => (x[0] === '' ? undefined : Number(x[0])) };
}

function stringOrUndefinedCodec(def: string | undefined): Codec<string | undefined> {
	return { def, encode: (x) => x ?? '', decode: (x) => (x[0] === '' ? undefined : x[0]) };
}

function booleanCodec(def: boolean): Codec<boolean> {
	return { def, encode: (x) => `${Number(x)}`, decode: (x) => Boolean(Number(x[0])) };
}

function jsonMultiCodec<T>(def: T[]): Codec<T[]> {
	return {
		def,
		encode: (xs) => (xs.length === 0 ? [''] : xs.map((x) => JSON.stringify(x))),
		decode: (xs) => (xs[0] === '' ? [] : xs.map((x) => JSON.parse(x))),
	};
}

const groupsCodec: Codec<AttrPath[]> = {
	def: [],
	encode: (groups: AttrPath[]) => groups.map((gr) => gr.join('.')).join(':'),
	decode: (g: string[]) => {
		if (!g[0]) return [];
		return g[0].split(':').map((group) => <AttrPath>group.split('.'));
	},
};

export const sortCodec: Codec<Sort> = {
	def: { active: '', direction: '' },
	encode: (sortOrder) => {
		return `${sortOrder.active}:${sortOrder.direction}`;
	},
	decode: (sortOrder) => {
		if (!sortOrder[0]) return { active: '', direction: '' };

		const [active, direction] = sortOrder[0].split(':');
		return <Sort>{ active, direction };
	},
};

// Legacy vakOrder waarden (Liquibase CL-8889) die nog in bestaande bookmarks of
// post-migratie aangemaakte favorieten/gedeelde URLs kunnen voorkomen.
const vakOrderCodec: Codec<Sort> = {
	def: { active: 'Tekortpunten', direction: 'desc' },
	encode: sortCodec.encode,
	decode: (sortOrder) => {
		if (!sortOrder[0]) return { active: '', direction: '' };

		const legacyMap: Record<string, Sort> = {
			'0': { active: 'Tekortpunten', direction: 'desc' },
			'1': { active: 'Tekortpunten', direction: 'asc' },
			'2': { active: 'Volgnummer', direction: 'asc' },
			'3': { active: 'Alfabetisch', direction: 'asc' },
		};
		if (sortOrder[0] in legacyMap) return legacyMap[sortOrder[0]];

		return sortCodec.decode(sortOrder);
	},
};

const datasnapResultCodec: Codec<DatasnapResultKey | undefined> = {
	def: undefined,
	encode: (key) => {
		if (key === undefined) return '';
		return encodeKey(key);
	},
	decode: (params) => {
		if (!params[0]) return undefined;
		const [urlId, resultId] = params[0].split(':');
		return { urlId, resultId };
	},
};

function attrPathCodec(def: AttrPath): Codec<AttrPath> {
	return { def, encode: (attrPath) => attrPath.join('.'), decode: (pathStr) => <AttrPath>pathStr[0].split('.') };
}

/**
 * nieuwe parameter? update de bugsnag allowlist:
 * console.log(['g', ...Object.keys(configs)]);
 */
const configs = {
	aanwezigheiduitsplitsing: singleCodec(AanwezigheidUitsplitsing.LEERLING),
	aspect: singleCodec(DashboardAspect.VERDELING),
	afwijkingpercentiel: singleCodec(AfwijkingPercentielWeergave.PERCENTIEL),
	'brin-overgang': booleanCodec(false),
	categorieuitstroomniveauvso: singleCodec(CategorieUitstroomniveauVSO.VERVOLGONDERWIJS),
	cijfersseceweergave: singleCodec(CijfersSeCeWeergave.VERSCHIL),
	cijfertype: singleCodec(CijferkolomKeuze.TOETS),
	chat: numberOrUndefinedCodec(undefined),
	cohortrendementtype: singleCodec<CohortrendementType>('Cohortrendement tot'),
	cohortrendementweergave: singleCodec<CohortrendementWeergave>('Cohortrendement'),
	col: multiCodec([CijferColumn.Cijfer, CijferColumn.Tekortpunten, CijferColumn.Leerlingen, CijferColumn.PercOnvoldoende]),
	doorstroomweergave: singleCodec(DoorstroomWeergave.SUCCESVOL),
	dossier: singleCodec(Dossier.VOORTGANG),
	'details-list': jsonMultiCodec<LeerlingSelectieId>([]),
	eenheid: singleCodec(Eenheid.AANTAL),
	'indicator-over': numberCodec(DRIE_SCHOOLJAREN_EXCL_2020),
	'inspectie-data': booleanCodec(true),
	interval: singleCodec(Interval.MAAND),
	kenmerk: attrPathCodec(['ds_is_apcg_van']),
	'lesuur-maand': numberOrUndefinedCodec(undefined),
	lockedColumn: stringOrUndefinedCodec(undefined),
	'or-uitsluiten': booleanCodec(true),
	'reden-maand': numberOrUndefinedCodec(undefined),
	selectie: numberOrUndefinedCodec(undefined),
	schooljaar: stringOrUndefinedCodec(undefined),
	sortOrder: sortCodec,
	sidebarSortOrder: sortCodec,
	snapshot: datasnapResultCodec,
	tekortpunten: numberCodec(0),
	tijdseenheid: singleCodec(Tijdseenheid.UREN),
	threshold: numberCodec(0),
	'toon-trend': booleanCodec(true),
	tussentijdseuitstroomvso: singleCodec(TussentijdseUitstroomVSO.REGULIER_ONDERWIJS),
	uitstroomniveauvso: singleCodec(VergelijkingUitstroomniveauVSO.OP_NIVEAU),
	vakOrder: vakOrderCodec,
	variant: singleCodec(DashboardVariant.ACTUEEL),
	verschilbeoogd: singleCodec(VerschilBeoogd.ABSOLUUT),
};

export type QpName = keyof typeof configs;

export type QpValue<N extends QpName> = (typeof configs)[N]['def'];

interface FixedWeergaveOptiesPerDashboard {
	[dashboardUrl: string]: string[];
}

/**
 * Utility om de toestand van dashboard-opties op te slaan in de URL query parameters.
 * Het patroon is als volgt:
 * - De optie is een state variabele in het dashboard
 * - Het component die de optie aanpast, past niet deze variabele aan, maar doet een "qp.dispatch(...)" (qp = deze service). Dit past de query parameter
 *   in de URL aan.
 * - Dashboard subscribet (qp.observe) op deze query parameter, en past de state variabele aan bij verandering.
 *
 * Voeg voor een nieuwe query parameter een entry toe hier in de "configs".
 */
@Injectable({
	providedIn: 'root',
})
export class QueryParamStateService {
	readonly currentUrlService = inject(CurrentUrlService);
	readonly router = inject(Router);
	readonly activatedRoute = inject(ActivatedRoute);
	readonly userService = inject(UserService);

	constructor() {
		if (isDevMode()) controleerQueryParamsUitRegistry(configs, this.router.config);
	}

	queryParams = toSignal(
		this.activatedRoute.queryParamMap.pipe(map((paramMap) => paramMap.keys.map((key) => ({ key, value: paramMap.get(key) }))))
	);

	private readonly allFixedWeergaveOpties = computed(() => this.getFixedWeergaveOptiesPerDashboard(this.router.config));

	private readonly currentFixedWeergaveOpties = computed(() => {
		const currentPath = this.currentUrlService.currentPath();
		if (!currentPath) return;

		return this.allFixedWeergaveOpties()[currentPath];
	});

	/** ingestelde queryparams/filters (excl. fixed weergaveopties) */
	private readonly filteredQueryParams = computed(() => {
		const queryParams = this.queryParams();
		if (!queryParams) return undefined;

		const currentFixedWeergaveOpties = this.currentFixedWeergaveOpties();
		if (!currentFixedWeergaveOpties) return undefined;

		return queryParams
			.filter(({ key }) => !currentFixedWeergaveOpties.includes(key))
			.filter(({ key, value }) => !(key === 'indicator-over' && value === '3.2'));
	});

	/** true als er queryparams of filters zijn ingesteld (afgezien van de fixed weergaveopties) */
	hasQueryParams = computed(() => {
		const queryParams = this.filteredQueryParams();
		return queryParams ? queryParams.length > 0 : false;
	});

	observe<T extends QpName>(paramName: T, overrideDefault?: QpValue<T>): Observable<QpValue<T>> {
		return this.activatedRoute.queryParamMap.pipe(
			map((paramMap) => paramMap.getAll(paramName)),
			distinctUntilChanged(isEqual),
			map((s) => {
				if (s.length > 0) return configs[paramName].decode(s);
				else return overrideDefault ?? configs[paramName].def;
			}),
			tap((decoded) => {
				const raw = this.activatedRoute.snapshot.queryParamMap.getAll(paramName);
				if (raw.length === 0) return;
				const codec = configs[paramName] as Codec<QpValue<T>>;
				const reencoded = codec.encode(decoded);
				const canonical = Array.isArray(reencoded) ? reencoded[0] : reencoded;
				if (raw[0] !== canonical) this.dispatch<T>(paramName, decoded, { replaceUrl: true });
			})
		);
	}

	/** gebruik voor aanwezigheiduitsplitsing het signal_aanwezigheiduitsplitsing verderop */
	signal<T extends Exclude<QpName, 'aanwezigheiduitsplitsing'>>(paramName: T, overrideDefault?: QpValue<T>): Signal<QpValue<T>> {
		return toSignal(this.observe(paramName, overrideDefault));
	}

	/** De huidige waarde van een queryparam zoals hij in de URL staat, voor de context van de AI-chat. */
	encodedSignal(paramName: Exclude<QpName, 'aanwezigheiduitsplitsing'>): Signal<string | undefined> {
		const codec = configs[paramName] as Codec<unknown>;
		return toSignal(
			this.observe(paramName).pipe(
				map((waarde) => {
					const encoded = codec.encode(waarde);
					return Array.isArray(encoded) ? encoded[0] : encoded;
				})
			)
		);
	}

	private defaultAanwezigheiduitsplitsing(): AanwezigheidUitsplitsing | undefined {
		const option = findDashboardOption('aanwezigheiduitsplitsing');
		const waarde = option && optionDefaultForRol(option, this.userService.activeRol());
		return waarde === option?.default ? undefined : (waarde as AanwezigheidUitsplitsing);
	}

	/**
	 * Ook een verzonnen waarde uit een URL valt terug op de default van de rol.
	 */
	private toegestaneAanwezigheiduitsplitsing(waarde: AanwezigheidUitsplitsing): AanwezigheidUitsplitsing {
		const option = findDashboardOption('aanwezigheiduitsplitsing');
		if (!option) return waarde;

		const rol = this.userService.activeRol();
		return optionValuesForRol(option, rol).includes(waarde) ? waarde : (optionDefaultForRol(option, rol) as AanwezigheidUitsplitsing);
	}

	signal_aanwezigheiduitsplitsing(): Signal<AanwezigheidUitsplitsing> {
		const observable = this.observe('aanwezigheiduitsplitsing', this.defaultAanwezigheiduitsplitsing()).pipe(
			map((waarde) => this.toegestaneAanwezigheiduitsplitsing(waarde))
		);
		return toSignal(observable, {
			initialValue: configs.aanwezigheiduitsplitsing.def, // deze initialValue komt in praktijk nooit voor (omdat de observable meteen emit), maar anders typeert het niet
		});
	}

	observe_g(): Observable<AttrPath[] | undefined> {
		return this.activatedRoute.queryParamMap.pipe(
			map((paramMap) => (paramMap.has('g') ? paramMap.getAll('g') : undefined)),
			distinctUntilChanged(isEqual),
			map((value) => (value ? groupsCodec.decode(value) : undefined))
		);
	}

	dispatch<T extends QpName>(paramName: T, value: QpValue<T> | undefined, extras: NavigationExtras = {}) {
		this.router.navigate([], {
			...extras,
			queryParams: { ...this.activatedRoute.snapshot.queryParams, ...this.encodeQueryParam(paramName, value) },
		});
	}

	dispatchMulti(params: Partial<{ [key in QpName]: QpValue<key> | undefined }>, extras: NavigationExtras = {}) {
		const newParams = Object.entries(params).map(([key, value]) => this.encodeQueryParam(key as QpName, value));
		this.router.navigate([], {
			...extras,
			queryParams: {
				...this.activatedRoute.snapshot.queryParams,
				...Object.assign({}, ...newParams),
			},
		});
	}

	dispatch_g(value: AttrPath[]) {
		this.router.navigate([], {
			queryParams: { ...this.activatedRoute.snapshot.queryParams, ...this.encodeGroupQueryParam(value) },
		});
	}

	encodeQueryParam<T extends QpName>(paramName: T, value: QpValue<T> | undefined): Params {
		return { [paramName]: value === undefined ? undefined : (<Codec<QpValue<T>>>configs[paramName]).encode(value) };
	}

	encodeGroupQueryParam(value: AttrPath[]): Params {
		return { ['g']: groupsCodec.encode(value) };
	}

	decodeGroupQueryParam(value: string): AttrPath[] {
		return groupsCodec.decode([value]);
	}

	resetQueryParams() {
		return this.router.navigate([], { queryParams: {} });
	}

	resetDashboard(): Promise<boolean> {
		const filteredQueryParams = this.filteredQueryParams();
		if (!filteredQueryParams) return Promise.resolve(true);

		return this.router.navigate([], {
			queryParams: this.getQueryParametersNulled(filteredQueryParams.map(({ key }) => key)),
			queryParamsHandling: 'merge',
		});
	}

	getAllQueryParamOptions() {
		return [...Object.keys(configs), 'g', 'adhoc', 'adhoc-docent', 'from'];
	}

	getNotThisDashboardQueryParamOptions(weergaveOpties?: string[]) {
		return this.getAllQueryParamOptions().filter((param) => !weergaveOpties?.includes(param));
	}

	getQueryParametersNulled(keys: string[]) {
		// maak object met null als value voor alle keys
		return Object.fromEntries(keys.map((key) => [key, null]));
	}

	private getFixedWeergaveOptiesPerDashboard(routes: CumLaudeRoute<UserRouteData>[], parentUrl: string[] = []): FixedWeergaveOptiesPerDashboard {
		const dashboardWeergaveOpties: FixedWeergaveOptiesPerDashboard = {};
		for (const route of routes) {
			const path = route.path;
			if (isUndefined(path)) continue;

			const url = [...parentUrl, path];

			const children = route.children;
			if (children) {
				Object.assign(dashboardWeergaveOpties, this.getFixedWeergaveOptiesPerDashboard(children, url));
			}

			const fixedWeergaveOpties = route.data?.fixedWeergaveOpties;
			if (fixedWeergaveOpties) {
				dashboardWeergaveOpties[url.join('/')] = fixedWeergaveOpties;
			}
		}
		return dashboardWeergaveOpties;
	}
}
